import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import {
  DataGridBody,
  DataGridRow,
  DataGrid,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridCell,
  TableCellLayout,
  createTableColumn,
  Tooltip,
  Button,
} from "@fluentui/react-components";
import { CopyRegular, CheckmarkRegular } from '@fluentui/react-icons';
import CurrentCustomerInformationDetails from './PagesComponents/CurrentCustomerInformationDetails';

const tooltipStyles = `
  .fui-Tooltip,
  [class*="Tooltip"] {
    background-color: #323130 !important;
    color: white !important;
    padding: 8px 12px !important;
    border-radius: 4px !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2) !important;
  }
  .sync-instance-container {
    margin: 20px auto;
    max-width: 1600px;
    padding-left: 40px;
    padding-right: 40px;
    box-sizing: border-box;
    width: 100%;
  }
`;

function TooltipCopyCell({ displayText, tooltipText, fullText }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(fullText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  return (
    <Tooltip
      relationship="description"
      content={
        <div style={{
          backgroundColor: '#323130',
          color: 'white',
          padding: '8px 12px',
          borderRadius: '4px',
          fontSize: '14px',
          maxWidth: '400px',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word'
        }}>
          <div style={{ marginBottom: '8px' }}>
            {tooltipText}
          </div>
          <Button
            size="small"
            appearance="subtle"
            icon={copied ? <CheckmarkRegular /> : <CopyRegular />}
            onClick={handleCopy}
            style={{
              backgroundColor: copied ? '#107c10' : 'transparent',
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              minWidth: 'auto',
              padding: '4px 8px'
            }}
          >
            {copied ? 'Copied!' : 'Copy'}
          </Button>
        </div>
      }
    >
      <span style={{ cursor: 'default' }}>{displayText}</span>
    </Tooltip>
  );
}

function SyncInstanceInformation() {
  const { tenantInt, instanceId, customerId } = useParams();
  const location = useLocation();
  const startedBy = location.state?.startedBy || null;
  const startDate = location.state?.startDate || null;
  const endDate = location.state?.endDate || null;
  const [details, setDetails] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!tenantInt || !instanceId) {
      setError('TenantInt and InstanceId are required');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    let url = `${process.env.REACT_APP_BACKEND_URL}/api/syncs/iteration-details?TenantInt=${tenantInt}&InstanceId=${encodeURIComponent(instanceId)}`;
    if (startedBy) {
      url += `&StartedBy=${encodeURIComponent(startedBy)}`;
    }

    fetch(url)
      .then((res) => {
        if (!res.ok) {
          throw new Error('Failed to fetch iteration details');
        }
        return res.json();
      })
      .then((data) => {
        setDetails(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to fetch iteration details:', err);
        setError(err.message);
        setLoading(false);
      });
  }, [tenantInt, instanceId, startedBy]);

  useEffect(() => {
    if (!customerId) {
      return;
    }

    const customerIdValue = parseInt(customerId, 10);
    const tenantIntValue = tenantInt ? parseInt(tenantInt, 10) : null;
    
    if (customerIdValue === 9999999 || tenantIntValue === 9999999) {
      setCustomer({
        CustomerID: 9999999,
        TenantInt: 9999999,
        CompanyName: "All Tenants Syncs - these syncs impact all customers",
        TenantName: "Global - these syncs impact all customers"
      });
    }
    else
    {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers/${customerId}`)
      .then((res) => {
        if (!res.ok) {
          if (res.status === 404) {
            throw new Error('Customer not found');
          }
          throw new Error('Failed to fetch customer information');
        }
        return res.json();
      })
      .then((data) => {
        setCustomer(data);
      })
      .catch((err) => {
        console.error('Failed to fetch customer information:', err);
      });
    }
  }, [customerId, tenantInt]);

  const columns = [
    createTableColumn({
      columnId: 'TableName',
      compare: (a, b) => (a.TableName || '').localeCompare(b.TableName || ''),
      renderHeaderCell: () => 'Impacted Tables',
      renderCell: (item) => (
        <TableCellLayout>{item.TableName || 'N/A'}</TableCellLayout>
      )
    }),
    createTableColumn({
      columnId: 'Rows',
      compare: (a, b) => (a.Rows || 0) - (b.Rows || 0),
      renderHeaderCell: () => 'Rows Affected',
      renderCell: (item) => (
        <TableCellLayout>{item.Rows ?? 0}</TableCellLayout>
      )
    }),
    createTableColumn({
      columnId: 'ErrorMessage',
      compare: (a, b) => (a.ErrorMessage || '').localeCompare(b.ErrorMessage || ''),
      renderHeaderCell: () => 'Error Message',
      renderCell: (item) => {
        const text = item.ErrorMessage;
        if (!text) return <TableCellLayout>-</TableCellLayout>;

        const displayText = text.length > 50 ? text.substring(0, 50) + '...' : text;
        const tooltipText = text.length > 250 ? text.substring(0, 250) + '...' : text;

        return (
          <TableCellLayout>
            <TooltipCopyCell 
              displayText={displayText}
              tooltipText={tooltipText}
              fullText={text}
            />
          </TableCellLayout>
        );
      }
    }),
    createTableColumn({
      columnId: 'ErrorDetails',
      compare: (a, b) => (a.ErrorDetails || '').localeCompare(b.ErrorDetails || ''),
      renderHeaderCell: () => 'Error Details',
      renderCell: (item) => {
        const text = item.ErrorDetails;
        if (!text) return <TableCellLayout>-</TableCellLayout>;

        const displayText = text.length > 50 ? text.substring(0, 50) + '...' : text;
        const tooltipText = text.length > 250 ? text.substring(0, 250) + '...' : text;

        return (
          <TableCellLayout>
            <TooltipCopyCell 
              displayText={displayText}
              tooltipText={tooltipText}
              fullText={text}
            />
          </TableCellLayout>
        );
      }
    }),
    createTableColumn({
      columnId: 'ErrorInnermostException',
      compare: (a, b) => (a.ErrorInnermostException || '').localeCompare(b.ErrorInnermostException || ''),
      renderHeaderCell: () => 'Error Exception',
      renderCell: (item) => {
        const text = item.ErrorInnermostException;
        if (!text) return <TableCellLayout>-</TableCellLayout>;

        const displayText = text.length > 50 ? text.substring(0, 50) + '...' : text;
        const tooltipText = text.length > 250 ? text.substring(0, 250) + '...' : text;

        return (
          <TableCellLayout>
            <TooltipCopyCell 
              displayText={displayText}
              tooltipText={tooltipText}
              fullText={text}
            />
          </TableCellLayout>
        );
      }
    }),
    createTableColumn({
      columnId: 'Status',
      compare: (a, b) => {
        const isFailedA = a.ErrorMessage != null 
          && a.ErrorMessage !== "No data to sync." 
          && a.ErrorDetails != null;
        const isFailedB = b.ErrorMessage != null 
          && b.ErrorMessage !== "No data to sync." 
          && b.ErrorDetails != null;
        const statusA = isFailedA ? 'Failed' : 'Success';
        const statusB = isFailedB ? 'Failed' : 'Success';
        return statusA.localeCompare(statusB);
      },
      renderHeaderCell: () => 'Status',
      renderCell: (item) => {
        const isFailed = item.ErrorMessage != null 
          && item.ErrorMessage !== "No data to sync." 
          && item.ErrorDetails != null;
        return (
          <TableCellLayout>
            <span style={{
              padding: '4px 8px',
              borderRadius: '4px',
              backgroundColor: isFailed ? '#d13438' : '#107c10',
              color: 'white',
              fontWeight: '500'
            }}>
              {isFailed ? 'Failed' : 'Success'}
            </span>
          </TableCellLayout>
        );
      }
    }),
  ];

  const backUrl = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    return `/sync-details/${tenantInt}/${customerId}${tabParam ? `?tab=${tabParam}` : ''}`;
  }, [location.search, tenantInt, customerId]);

  return (
    <div className="sync-instance-container">
      <style>{tooltipStyles}</style>
      <h1>Sync Instance Information</h1>
      <Link
        to={backUrl}
        style={{
          display: 'inline-block',
          marginBottom: '20px',
          padding: '8px 16px',
          backgroundColor: '#0078d4',
          color: 'white',
          textDecoration: 'none',
          borderRadius: '4px',
          fontSize: '14px'
        }}
      >
        Back to Sync Details
      </Link>

      <CurrentCustomerInformationDetails 
        customer={customer} 
        startedBy={startedBy} 
        instanceId={instanceId}
        startDate={startDate}
        endDate={endDate}
      />

      {loading && <p>Loading...</p>}
      {error && <p style={{ color: 'red' }}>Error: {error}</p>}
      {!loading && !error && details.length === 0 && (
        <p>No data found for this sync instance.</p>
      )}
      {!loading && !error && details.length > 0 && (
        <div>
          <div className="no-clickable-rows" style={{
            border: '1px solid #e1dfdd',
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
          <DataGrid
            items={details}
            columns={columns}
            sortable
            noNativeElements={false}
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              tableLayout: 'auto'
            }}
          >
            <DataGridHeader>
              <DataGridRow style={{ borderBottom: '2px solid #e1dfdd' }}>
                {({ renderHeaderCell }) => (
                  <DataGridHeaderCell
                    style={{
                      borderRight: '1px solid #e1dfdd',
                      padding: '2px 4px',
                      backgroundColor: '#f3f2f1',
                      fontWeight: '600'
                    }}
                  >
                    {renderHeaderCell()}
                  </DataGridHeaderCell>
                )}
              </DataGridRow>
            </DataGridHeader>
            <DataGridBody>
              {({ item, rowId }) => (
                <DataGridRow
                  key={rowId}
                  style={{ borderBottom: '1px solid #e1dfdd' }}
                >
                  {({ renderCell }) => (
                    <DataGridCell
                      style={{
                        borderRight: '1px solid #e1dfdd',
                        padding: '2px 4px'
                      }}
                    >
                      {renderCell(item)}
                    </DataGridCell>
                  )}
                </DataGridRow>
              )}
            </DataGridBody>
          </DataGrid>
          </div>
        </div>
      )}
    </div>
  );
}

export default SyncInstanceInformation;
